import React, { useState, useEffect, useRef } from 'react';
import { PortfolioEditor, PublicPortfolio } from '../components/portfolio/MyPortfolio';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSectionsVisibility } from '../hooks/useSectionsVisibility';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, Settings as SettingsIcon, UserPlus, UserMinus, UserCheck, 
  Heart, Play, Pause, Users, Music, Podcast, Trash2, TrendingUp, Edit, Bell, Bookmark, Plus, Video, Eye, ExternalLink, Shield, BookOpen, Lock, Package, Download, Camera, Wifi, Image as ImageIcon, X, Flame
} from 'lucide-react';
import Navigation from '../components/Navigation';
import AudioPlayer from '../components/AudioPlayer';
import EditProfileModal from '../components/EditProfileModal';
import ImageModal from '../components/ImageModal';
import FollowersModal from '../components/FollowersModal';
import MyEsimsPanel from '../components/MyEsimsPanel';
// AddStoryModal is now mounted inside StoryBar on the Home tab.
import EDepotsSection from '../components/EDepotsSection';
import PromoteProfileModal from '../components/PromoteProfileModal';
import PhotoPostCard from '../components/photos/PhotoPostCard';
import VideoPostCard from '../components/videos/VideoPostCard';
import { videoTexts } from '../components/videos/videoTranslations';
import { getMediaUrl } from '../utils/mediaUrl';
import VerifiedBadge from '../components/VerifiedBadge';

const Profile = () => {
  // Fixed image loading - v2
  const { userId, username } = useParams();
  const { user: currentUser } = useAuth();
  const { isDark } = useTheme();
  const { homeHidden } = useSectionsVisibility();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // If the route is /profile/u/:username we resolve the username -> user id
  // once on mount. The rest of the page uses `resolvedUserId` from that point.
  const [resolvedUserId, setResolvedUserId] = useState(userId || null);
  const [resolveError, setResolveError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (username) {
      (async () => {
        try {
          const res = await axios.get('/api/users/mentions/search', { params: { q: username } });
          const match = (res.data?.users || []).find(u => (u.username || '').toLowerCase() === username.toLowerCase());
          if (!cancelled) {
            if (match) setResolvedUserId(match.id);
            else setResolveError(true);
          }
        } catch (_) {
          if (!cancelled) setResolveError(true);
        }
      })();
    } else {
      setResolvedUserId(userId || null);
    }
    return () => { cancelled = true; };
  }, [username, userId]);
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followersModalType, setFollowersModalType] = useState('followers');
  const [currentPlayingId, setCurrentPlayingId] = useState(null);
  const [hasActiveStory, setHasActiveStory] = useState(false);
  const [userShops, setUserShops] = useState([]);

  const isOwnProfile = !resolvedUserId || resolvedUserId === currentUser?.id;
  useEffect(() => {
    if (homeHidden && ['posts', 'podcasts', 'photos', 'shared_videos', 'saved', 'none'].includes(activeTab)) {
      const next = isOwnProfile ? 'edepots' : (userShops.length ? 'shop' : 'none');
      if (next !== activeTab) setActiveTab(next);
    }
    // eslint-disable-next-line
  }, [homeHidden, isOwnProfile, userShops.length]);
  const targetUserId = resolvedUserId || currentUser?.id;

  useEffect(() => {
    loadProfile();
    checkForStories();
    if (isOwnProfile) {
      loadUnreadCount();
    }
  }, [targetUserId]);

  // Load user's shops (both own & public profile — for the MyShop tab)
  useEffect(() => {
    if (!targetUserId) { setUserShops([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`/api/shop/shops/by-user/${targetUserId}`);
        if (!cancelled) setUserShops(r.data?.shops || []);
      } catch (_) {
        if (!cancelled) setUserShops([]);
      }
    })();
    return () => { cancelled = true; };
  }, [targetUserId]);

  const checkForStories = async () => {
    try {
      const response = await axios.get(`/api/stories/user/${targetUserId}`);
      setHasActiveStory(response.data.stories && response.data.stories.length > 0);
    } catch (error) {
      setHasActiveStory(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await axios.get('/api/notifications/unread-count');
      setUnreadNotifications(response.data.count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/users/${targetUserId}/profile`);
      const profileData = response.data;
      // Audio stories feature deprecated — no longer fetched
      profileData.audio_stories = [];
      setProfile(profileData);
      if (!isOwnProfile && profileData.account_type === 'PRO') {
        axios.post('/api/ads-commercial/track', { kind: 'profile_view', target_user_id: targetUserId }).catch(() => {});
      }
      
      // Check if current user follows this profile
      if (currentUser && !isOwnProfile) {
        const currentUserData = await axios.get(`/api/users/${currentUser.id}`);
        setFollowing(currentUserData.data.following?.includes(targetUserId) || false);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Erreur de chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    try {
      const response = await axios.post(`/api/users/${targetUserId}/follow`);
      setFollowing(response.data.following);
      
      // Update followers count
      setProfile({
        ...profile,
        stats: {
          ...profile.stats,
          followers_count: profile.stats.followers_count + (response.data.following ? 1 : -1)
        }
      });
      
      toast.success(response.data.following ? 'Vous suivez maintenant cet utilisateur' : 'Vous ne suivez plus cet utilisateur');
    } catch (error) {
      console.error('Error following user:', error);
      toast.error('Erreur');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce post ?')) return;
    
    try {
      await axios.delete(`/api/gim-toli/${postId}`);
      toast.success('Post supprimé');
      loadProfile();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePlayStateChange = (postId, isPlaying) => {
    if (isPlaying) {
      // Arrêter tous les autres lecteurs et jouer celui-ci
      setCurrentPlayingId(postId);
    } else {
      // Arrêter uniquement si c'est le lecteur actuel
      setCurrentPlayingId(prev => prev === postId ? null : prev);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center pb-20">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`min-h-screen flex items-center justify-center pb-20 ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
        <div className="text-white text-xl">Profil introuvable</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen text-white pb-20 ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="bg-white/5 rounded-3xl border border-white/10 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-4">
              {/* Photo de profil avec indicateur de story */}
              <div className="relative">
                <div 
                  className={`w-20 h-20 rounded-full flex items-center justify-center overflow-hidden cursor-pointer transition-all ${
                    hasActiveStory 
                      ? 'ring-4 ring-gradient-to-r from-violet-500 to-magenta-500 p-[3px]' 
                      : 'bg-gradient-to-br from-magenta-500 to-violet-600'
                  }`}
                  onClick={() => hasActiveStory ? navigate(homeHidden ? `/films?story_user=${profile.id}` : '/feed') : profile.profile_image_url && setShowImageModal(true)}
                  title={hasActiveStory ? "Voir la story sur l'accueil" : profile.profile_image_url ? "Voir la photo de profil" : ""}
                >
                  <div className={`w-full h-full rounded-full overflow-hidden ${hasActiveStory ? 'ring-2 ring-black' : ''}`}>
                    {profile.profile_image_url ? (
                      <img
                        src={getMediaUrl(profile.profile_image_url)}
                        alt={profile.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error('Profile image error:', profile.profile_image_url);
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-magenta-500 to-violet-600 flex items-center justify-center">
                        <span className="text-3xl font-bold">{profile.name?.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h1 className="text-2xl font-bold flex items-center gap-1.5">
                  {profile.name}
                  {profile.is_verified && <VerifiedBadge size="lg" />}
                </h1>
                <p className="text-gray-400">@{profile.username}</p>
                {profile.account_type === 'PRO' && (
                  <span className="inline-block mt-1 px-3 py-1 bg-magenta-500/20 text-magenta-400 rounded-full text-xs font-semibold">
                    COMPTE PRO
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex flex-col items-end space-y-2">
              {isOwnProfile ? (
                <>
                  <button
                    onClick={() => navigate('/notifications')}
                    className="relative p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                    title="Notifications"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-magenta-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                    title="Modifier le profil"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => navigate('/settings')}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                    title="Paramètres"
                  >
                    <SettingsIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowPromoteModal(true)}
                    className="p-2 bg-gradient-to-br from-orange-500 to-magenta-600 hover:from-orange-600 hover:to-magenta-700 rounded-full transition-colors shadow-lg shadow-orange-500/30"
                    title={t('promote.buttonTitle') || 'Promouvoir mon profil'}
                    data-testid="promote-profile-flame-btn"
                  >
                    <Flame className="w-5 h-5 text-white" />
                  </button>
                </>
              ) : (
                null
              )}
              
              {/* Bouton Destine Ads — visible sur SON PROPRE profil pour PRO ou super_admin */}
              {isOwnProfile && (profile.account_type === 'PRO' || profile.is_super_admin || profile.is_admin) && (
                <button
                  onClick={() => navigate('/pro-space')}
                  className="px-4 py-2 bg-violet-500 hover:bg-violet-600 rounded-full font-semibold transition-colors flex items-center space-x-2"
                  data-testid="destine-studio-ads-btn"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>Destine Ads</span>
                </button>
              )}
            </div>
          </div>

          {profile.bio && (
            <p className="text-gray-300 mb-4">{profile.bio}</p>
          )}

          {/* Website link — available for all account types (ID + PRO) */}
          {profile.website_url && (
            <a
              href={profile.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mb-4 text-sm text-cyan-300 hover:text-cyan-200"
              data-testid="profile-website-link"
              onClick={() => { if (!isOwnProfile) axios.post('/api/ads-commercial/track', { kind: 'link_click', target_user_id: targetUserId }).catch(() => {}); }}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {profile.website_url.replace(/^https?:\/\//, '')}
            </a>
          )}

          {/* Bio Audio */}
          {profile.bio_audio_url && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-400 mb-2">{t('profile.bioAudio')}</h3>
              <audio
                controls
                src={getMediaUrl(profile.bio_audio_url)}
                className="w-full h-10 rounded-xl"
                style={{ filter: 'brightness(0.8)' }}
                onError={(e) => console.error('Bio audio error:', profile.bio_audio_url)}
              />
            </div>
          )}

          {/* Action buttons — Message + Follow (only on other users' profiles).
              Moved here from the cramped top-right corner so they breathe better
              and sit naturally under the presentation audio. */}
          {!isOwnProfile && (
            <div className="flex gap-2 mb-4" data-testid="profile-action-buttons">
              <button
                onClick={() => navigate(`/messages/new/${profile.id}`)}
                className="flex-1 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 rounded-full font-semibold transition-colors flex items-center justify-center gap-2"
                data-testid="profile-message-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <span>{t('profileActions.message', 'Message')}</span>
              </button>
              <button
                onClick={handleFollow}
                className={`flex-1 px-4 py-2.5 rounded-full font-semibold transition-colors flex items-center justify-center gap-2 ${
                  following
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                    : 'bg-gradient-to-r from-magenta-500 to-violet-600 hover:from-magenta-600 hover:to-violet-700 text-white'
                }`}
                data-testid="profile-follow-btn"
              >
                {following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                <span>{following ? t('profileActions.following', 'Suivi') : t('profileActions.follow', 'Suivre')}</span>
              </button>
            </div>
          )}

          {/* Stats — mode « Accueil masqué » : vues & likes des stories, abonnés, abonnements */}
          {homeHidden ? (
          <div className="grid grid-cols-4 gap-2 pt-4 border-t border-white/10" data-testid="profile-stats-stories-only">
            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-400">{profile.stats?.total_story_views || 0}</div>
              <div className="text-xs text-gray-400">{t("profile.followers") === "Followers" ? "Views" : "Vues"}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-violet-400">{profile.stats?.total_story_likes || 0}</div>
              <div className="text-xs text-gray-400">Likes</div>
            </div>
            <button className="text-center hover:bg-white/5 rounded-lg transition-colors p-1" onClick={() => { setFollowersModalType('followers'); setShowFollowersModal(true); }}>
              <div className="text-2xl font-bold text-blue-400">{profile.stats?.followers_count || 0}</div>
              <div className="text-xs text-gray-400">{t("profile.followers")}</div>
            </button>
            <button className="text-center hover:bg-white/5 rounded-lg transition-colors p-1" onClick={() => { setFollowersModalType('following'); setShowFollowersModal(true); }}>
              <div className="text-2xl font-bold text-green-400">{profile.stats?.following_count || 0}</div>
              <div className="text-xs text-gray-400">{t("profile.following")}</div>
            </button>
          </div>
          ) : (<>
          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/10">
            <div className="text-center">
              <div className="text-2xl font-bold text-magenta-400">{profile.stats?.posts_count || 0}</div>
              <div className="text-xs text-gray-400">{t("profile.posts")}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-violet-400">{profile.stats?.total_likes || 0}</div>
              <div className="text-xs text-gray-400">Likes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-400">{profile.stats?.total_video_views || 0}</div>
              <div className="text-xs text-gray-400">{t("profile.followers") === "Followers" ? "Views" : "Vues"}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 pt-2">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-400">{profile.stats?.total_gimtoli_plays || 0}</div>
              <div className="text-xs text-gray-400">{t("profile.followers") === "Followers" ? "Plays" : "Lectures"}</div>
            </div>
            <button 
              className="text-center hover:bg-white/5 rounded-lg transition-colors p-2"
              onClick={() => {
                setFollowersModalType('followers');
                setShowFollowersModal(true);
              }}
            >
              <div className="text-2xl font-bold text-blue-400">{profile.stats?.followers_count || 0}</div>
              <div className="text-xs text-gray-400">{t("profile.followers")}</div>
            </button>
            <button 
              className="text-center hover:bg-white/5 rounded-lg transition-colors p-2"
              onClick={() => {
                setFollowersModalType('following');
                setShowFollowersModal(true);
              }}
            >
              <div className="text-2xl font-bold text-green-400">{profile.stats?.following_count || 0}</div>
              <div className="text-xs text-gray-400">{t("profile.following")}</div>
            </button>
          </div>
          </>)}
        </div>

        {/* Accueil masqué : pas de sections de contenus partagés — grande photo de profil */}
        {homeHidden && !isOwnProfile && (
          <div className="rounded-3xl overflow-hidden border border-white/10 bg-white/5 mb-6" data-testid="profile-big-photo">
            {profile.profile_image_url ? (
              <img src={getMediaUrl(profile.profile_image_url)} alt={profile.name} className="w-full aspect-square object-cover" onClick={() => setShowImageModal(true)} />
            ) : (
              <div className="w-full aspect-square flex items-center justify-center bg-gradient-to-br from-fuchsia-600/40 to-violet-700/40">
                <span className="text-7xl font-bold">{(profile.name || '?')[0]?.toUpperCase()}</span>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto" data-testid="profile-tabs-row">
          {!homeHidden && (<>
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 ${
              activeTab === 'posts'
                ? 'bg-magenta-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <Music className="w-4 h-4 inline mr-2" />
            Gim Toli ({profile.posts?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('podcasts')}
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 ${
              activeTab === 'podcasts'
                ? 'bg-violet-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <Podcast className="w-4 h-4 inline mr-2" />
            {t('profile.podcasts')} ({profile.podcasts?.length || 0})
          </button>
          {/* Photos partagées tab (v50.14 — replaces Vidéo Toli) */}
          <button
            onClick={() => setActiveTab('photos')}
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 ${
              activeTab === 'photos'
                ? 'bg-pink-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="profile-tab-photos"
          >
            <Camera className="w-4 h-4 inline mr-2" />
            {t('profile.photos') || 'Photos'} ({profile.photos?.length || 0})
          </button>
          {/* Vidéos partagées tab (v50.10.7) */}
          <button
            onClick={() => setActiveTab('shared_videos')}
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 ${
              activeTab === 'shared_videos'
                ? 'bg-cyan-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="profile-tab-videos"
          >
            <Video className="w-4 h-4 inline mr-2" />
            {videoTexts(language).videosTab} ({profile.shared_videos?.length || 0})
          </button>
          </>)}
          {/* Audio stories tab removed — feature deprecated */}
          {isOwnProfile && !homeHidden && (
          <button
            onClick={() => setActiveTab('saved')}
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 ${
              activeTab === 'saved'
                ? 'bg-yellow-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="profile-tab-saved"
          >
            <Bookmark className="w-4 h-4 inline mr-2" />
            {t('profile.tab_saved') || 'Enregistrements'}
          </button>
          )}
          {/* E-dépôts tab — protégez vos œuvres (SACD-like). Own profile only. */}
          {isOwnProfile && (
          <button
            onClick={() => setActiveTab('edepots')}
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 ${
              activeTab === 'edepots'
                ? 'bg-fuchsia-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="profile-tab-edepots"
          >
            <Shield className="w-4 h-4 inline mr-2" />
            {t('profile.tab_edepots') || 'e-Dépôts'}
          </button>
          )}
          {/* Mes commandes — buyer MyShop orders + claims (own profile only) */}
          {isOwnProfile && (
          <button
            onClick={() => navigate('/shop/my-orders')}
            className="px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 bg-white/5 text-gray-400 hover:bg-white/10"
            data-testid="profile-tab-my-orders"
          >
            <Package className="w-4 h-4 inline mr-2" />
            {t('eshop.my_orders', 'Mes commandes')}
          </button>
          )}
          {/* Mes billets MyMouv */}
          {isOwnProfile && (
          <button
            onClick={() => navigate('/mymouv/my-tickets')}
            className="px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 bg-white/5 text-gray-400 hover:bg-white/10"
            data-testid="profile-tab-mymouv-tickets"
          >
            🎫 Mes billets MyMouv
          </button>
          )}
          {/* Mes eSIM */}
          {isOwnProfile && (
          <button
            onClick={() => setActiveTab('esims')}
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 ${
              activeTab === 'esims'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="profile-tab-esims"
          >
            <Wifi className="w-4 h-4 inline mr-2" />
            {t('esim.mySectionTitle')}
          </button>
          )}
          {/* MyShop tab — visible on own profile AND public profile (only if user has at least 1 shop). Right-most. */}
          {userShops.length > 0 && (
          <button
            onClick={() => setActiveTab('shop')}
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 ${
              activeTab === 'shop'
                ? 'bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="profile-tab-shop"
          >
            🛒 {t('profileActions.shopTab', 'MyShop')} ({userShops.length})
          </button>
          )}
          {/* v60.5 — MyPortfolio (après MyShop) : propriétaire toujours ; visiteurs si public / pro_only */}
          {(isOwnProfile || profile?.portfolio_visibility === 'public' || (profile?.portfolio_visibility === 'pro_only' && currentUser?.account_type === 'PRO')) && (
          <button
            onClick={() => setActiveTab('portfolio')}
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex-shrink-0 ${
              activeTab === 'portfolio'
                ? 'bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="profile-tab-portfolio"
          >
            💼 MyPortfolio
          </button>
          )}
        </div>

        {/* Content */}
        <div className="space-y-4" data-testid="profile-tab-content">
          {activeTab === 'portfolio' && (
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid="profile-portfolio-section">
              {isOwnProfile ? <PortfolioEditor /> : <PublicPortfolio userId={targetUserId} />}
            </div>
          )}
          {activeTab === 'shop' && (
            userShops.length === 0 ? (
              <div className="text-center py-12 text-white/40">Aucune boutique.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="profile-shops-grid">
                {userShops.map(shop => (
                  <button
                    key={shop.id}
                    onClick={() => navigate(`/shop/${shop.id}`)}
                    className="text-left rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 overflow-hidden transition-colors"
                    data-testid={`profile-shop-card-${shop.id}`}
                  >
                    {shop.logo_url && (
                      <img src={shop.logo_url} alt={shop.name} className="w-full aspect-video object-cover" />
                    )}
                    <div className="p-3">
                      <div className="font-bold text-white truncate">{shop.name}</div>
                      <div className="text-xs text-white/60 truncate">{shop.category || shop.city || ''}</div>
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-white/50">
                        {typeof shop.avg_stars === 'number' && shop.avg_stars > 0 && (
                          <span>★ {shop.avg_stars.toFixed(1)}</span>
                        )}
                        <span className="text-fuchsia-300">{t('profileActions.shopTab', 'MyShop')}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )
          )}
          {activeTab === 'esims' && isOwnProfile && (
            <MyEsimsPanel embedded />
          )}
          {activeTab === 'posts' && (
            profile.posts && profile.posts.length > 0 ? (
              profile.posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  showDelete={isOwnProfile}
                  onDelete={() => handleDeletePost(post.id)}
                  onReload={loadProfile}
                  isPlaying={currentPlayingId === post.id}
                  onPlayStateChange={(isPlaying) => handlePlayStateChange(post.id, isPlaying)}
                />
              ))
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Music className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun post pour le moment</p>
              </div>
            )
          )}

          {activeTab === 'podcasts' && (
            profile.podcasts && profile.podcasts.length > 0 ? (
              profile.podcasts.map((podcast) => (
                <PodcastCard 
                  key={podcast.id} 
                  podcast={podcast} 
                  isOwnProfile={isOwnProfile}
                  onDelete={loadProfile}
                />
              ))
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Podcast className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun podcast pour le moment</p>
              </div>
            )
          )}

          {/* Audio stories tab removed — feature deprecated */}
          {activeTab === 'photos' && (
            profile.photos && profile.photos.length > 0 ? (
              <div className="space-y-6" data-testid="profile-photos-grid">
                {profile.photos.map((photo) => (
                  <PhotoPostCard key={photo.id} post={{ ...photo, user: profile }} onUpdate={loadProfile} showDelete={isOwnProfile} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t('profile.photos') || 'Photos'} — 0</p>
              </div>
            )
          )}

          {activeTab === 'shared_videos' && (
            profile.shared_videos && profile.shared_videos.length > 0 ? (
              <div className="space-y-6" data-testid="profile-videos-grid">
                {profile.shared_videos.map((v) => (
                  <VideoPostCard key={v.id} post={{ ...v, user: profile }} onUpdate={loadProfile} showDelete={isOwnProfile} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400" data-testid="profile-videos-empty">
                <Video className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{videoTexts(language).noVideos}</p>
              </div>
            )
          )}

          {activeTab === 'saved' && isOwnProfile && (
            <SavedPostsSection userId={currentUser?.id} />
          )}

          {activeTab === 'edepots' && isOwnProfile && (
            <EDepotsSection currentUser={currentUser} />
          )}
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <EditProfileModal
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            loadProfile();
            toast.success('Profil mis à jour !');
          }}
          currentUser={profile}
        />
      )}

      {/* Promote Profile Modal */}
      {showPromoteModal && (
        <PromoteProfileModal
          onClose={() => setShowPromoteModal(false)}
        />
      )}

      {/* Image Modal */}
      {showImageModal && profile.profile_image_url && (
        <ImageModal
          imageUrl={getMediaUrl(profile.profile_image_url)}
          altText={`Photo de profil de ${profile.name}`}
          onClose={() => setShowImageModal(false)}
        />
      )}

      {/* Followers Modal */}
      {showFollowersModal && (
        <FollowersModal
          userId={targetUserId}
          type={followersModalType}
          onClose={() => setShowFollowersModal(false)}
        />
      )}

      {/* Story creation modal moved to StoryBar (Home tab) — no longer rendered here */}

      <Navigation />
    </div>
  );
};

const PostCard = ({ post, showDelete, onDelete, onReload, showUnsaveButton = false, onUnsave, isPlaying = false, onPlayStateChange }) => {
  const [localIsPlaying, setLocalIsPlaying] = useState(false);
  
  // Use external isPlaying state if provided, otherwise use local state
  const currentIsPlaying = onPlayStateChange ? isPlaying : localIsPlaying;
  const handlePlayStateChange = onPlayStateChange || setLocalIsPlaying;

  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-magenta-500/30 transition-all relative">
      {post.background_image_url && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-70"
            style={{ backgroundImage: `url(${getMediaUrl(post.background_image_url)})` }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" aria-hidden="true" />
        </>
      )}
      <div className={`${post.background_image_url ? 'relative z-10' : ''}`} {...(post.background_image_url ? { 'data-keep-dark': '' } : {})}>
      {/* Cover Image */}
      {post.cover_image_url && (
        <div className="relative aspect-video bg-gray-900">
          <img
            src={getMediaUrl(post.cover_image_url)}
            alt="Cover"
            className="w-full h-full object-cover"
            onError={(e) => {
              console.error('Post cover error:', post.cover_image_url);
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Action Buttons - Stacked vertically at top right to avoid overlapping the title */}
      <div className="absolute top-2 right-2 flex flex-col items-end space-y-2 z-10">
        {showDelete && (
          <button
            onClick={onDelete}
            className="p-2 bg-black/50 backdrop-blur-sm text-red-400 hover:bg-red-500/20 rounded-full transition-colors"
            title="Supprimer"
            data-testid={`delete-gimtoli-${post.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        {showDelete && (
          <>
          <button
            onClick={async () => {
              const newTitle = window.prompt('Nouveau titre :', post.title || '');
              if (newTitle === null) return;
              const trimmed = newTitle.trim();
              if (!trimmed) return toast.error('Titre vide');
              try {
                await axios.patch(`/api/gim-toli/${post.id}`, { title: trimmed });
                toast.success('Titre mis à jour');
                onReload?.(); // refresh list without triggering the delete-confirm dialog
              } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
            }}
            className="p-2 bg-black/50 backdrop-blur-sm text-blue-300 hover:bg-blue-500/20 rounded-full transition-colors"
            title="Renommer"
            data-testid={`rename-gimtoli-${post.id}`}
          >
            <Edit className="w-4 h-4" />
          </button>
          {/* Change / remove background image */}
          <label
            className="p-2 bg-black/50 backdrop-blur-sm text-fuchsia-300 hover:bg-fuchsia-500/20 rounded-full transition-colors cursor-pointer"
            title="Modifier l'image de fond"
            data-testid={`gimtoli-bg-edit-${post.id}`}
          >
            <ImageIcon className="w-4 h-4" />
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append('background_image', f);
                try {
                  await axios.post(`/api/gim-toli/${post.id}/background`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                  toast.success('Image de fond mise à jour');
                  onReload?.();
                } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
                e.target.value = '';
              }}
            />
          </label>
          {post.background_image_url && (
            <button
              onClick={async () => {
                if (!window.confirm("Retirer l'image de fond ?")) return;
                const fd = new FormData();
                fd.append('remove', 'true');
                try {
                  await axios.post(`/api/gim-toli/${post.id}/background`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                  toast.success('Image de fond retirée');
                  onReload?.();
                } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
              }}
              className="p-2 bg-black/50 backdrop-blur-sm text-red-300 hover:bg-red-500/20 rounded-full transition-colors"
              title="Retirer l'image de fond"
              data-testid={`gimtoli-bg-delete-${post.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
          </>
        )}
        {showUnsaveButton && (
          <button
            onClick={onUnsave}
            className="p-2 bg-black/50 backdrop-blur-sm text-yellow-400 hover:bg-yellow-500/20 rounded-full transition-colors"
            title="Retirer des enregistrements"
          >
            <Bookmark className="w-4 h-4 fill-current" />
          </button>
        )}
      </div>

      {/* Audio Player */}
      <div className="p-4">
        {post.title && (
          <h3 className="text-lg font-semibold mb-3 pr-12">{post.title}</h3>
        )}
        
        <AudioPlayer
          audioUrl={post.audio_url}
          isPlaying={currentIsPlaying}
          onPlayStateChange={handlePlayStateChange}
          transcription={post.transcription}
          transcriptionWords={post.transcription_words}
        />
      </div>

      {/* Stats */}
      <div className="px-4 pb-4 flex items-center space-x-6 text-sm text-gray-400">
        <div className="flex items-center space-x-1">
          <Heart className="w-4 h-4" />
          <span>{post.likes_count || 0}</span>
        </div>
        <div className="flex items-center space-x-1">
          <Play className="w-4 h-4" />
          <span>{post.plays_count || 0}</span>
        </div>
      </div>
      </div>
    </div>
  );
};

const SavedPostsSection = ({ userId }) => {
  const [savedPosts, setSavedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlayingId, setCurrentPlayingId] = useState(null);

  useEffect(() => {
    loadSavedPosts();
  }, [userId]);

  const loadSavedPosts = async () => {
    try {
      const response = await axios.get('/api/me/saved-all');
      setSavedPosts(response.data || []);
    } catch (error) {
      console.error('Error loading saved items:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (entry) => {
    try {
      if (entry.type === 'shop') {
        await axios.post(`/api/shop/shops/${entry.id}/save`);
      } else {
        const map = { gim_toli: 'gim-toli', short_film: 'videos', video: 'videos', photo: 'photos', podcast: 'podcasts', studio_story: 'studio/stories' };
        const path = map[entry.type] || 'gim-toli';
        await axios.post(`/api/${path}/${entry.id}/save`);
      }
      setSavedPosts(savedPosts.filter(p => !(p.id === entry.id && p.type === entry.type)));
      toast.success("Retiré des enregistrements");
    } catch (error) {
      console.error('Error unsaving:', error);
      toast.error('Erreur');
    }
  };

  const handlePlayStateChange = (postId, isPlaying) => {
    if (isPlaying) {
      // Arrêter tous les autres lecteurs et jouer celui-ci
      setCurrentPlayingId(postId);
    } else {
      // Arrêter uniquement si c'est le lecteur actuel
      setCurrentPlayingId(prev => prev === postId ? null : prev);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-gray-400">
        Chargement...
      </div>
    );
  }

  if (savedPosts.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Bookmark className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Aucun enregistrement</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="saved-items-list">
      {savedPosts.map((entry) => {
        const item = entry.item || {};
        const typeLabel = {
          gim_toli: 'Gim Toli',
          short_film: 'Film court',
          video: 'Vidéo partagée',
          photo: 'Photos partagées',
          podcast: 'Podcast',
          studio_story: 'Histoire audio',
          shop: 'Boutique',
        }[entry.type] || entry.type;
        if (entry.type === 'gim_toli') {
          return (
            <PostCard
              key={`${entry.type}-${entry.id}`}
              post={item}
              showDelete={false}
              onDelete={() => {}}
              onUnsave={() => handleUnsave(entry)}
              showUnsaveButton={true}
              isPlaying={currentPlayingId === entry.id}
              onPlayStateChange={(isPlaying) => handlePlayStateChange(entry.id, isPlaying)}
            />
          );
        }
        return (
          <div key={`${entry.type}-${entry.id}`}
            className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3 hover:bg-white/10 cursor-pointer transition-colors"
            data-testid={`saved-item-${entry.id}`}
            onClick={() => {
              if (entry.type === 'shop') window.location.href = `/shop/${entry.id}`;
              else if (entry.type === 'video' || entry.type === 'photo') window.location.href = `/feed?post=${entry.id}`;
            }}
          >
            <Bookmark className="w-5 h-5 text-fuchsia-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-fuchsia-300/80 uppercase tracking-wider">{typeLabel}</p>
              <p className="font-semibold truncate">{entry.title || 'Sans titre'}</p>
              {entry.saved_at && <p className="text-[11px] text-gray-400 mt-0.5">Enregistré le {new Date(entry.saved_at).toLocaleString('fr-FR')}</p>}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); handleUnsave(entry); }}
              className="px-3 py-1.5 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-medium"
              data-testid={`saved-item-remove-${entry.id}`}
            >
              Retirer
            </button>
          </div>
        );
      })}
    </div>
  );
};

const PodcastCard = ({ podcast, isOwnProfile, onDelete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Locally-overridden cover URL with cache-busting `?v=` so the new image
  // is rendered immediately after upload (browsers / Capacitor WebView would
  // otherwise re-use the cached old blob).
  const [localCoverUrl, setLocalCoverUrl] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const audioRef = useRef(null);
  const coverInputRef = useRef(null);

  const handleCoverPick = () => {
    if (coverUploading) return;
    coverInputRef.current?.click();
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file twice in a row
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 5 Mo)');
      return;
    }
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append('cover_image', file);
      const res = await axios.post(`/api/podcasts/${podcast.id}/cover`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.cache_busted_url || `${res.data?.cover_image_url}?v=${Date.now()}`;
      setLocalCoverUrl(url);
      toast.success('Image de couverture mise à jour');
      onDelete?.(); // refresh parent list so a re-mount also picks the new URL
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload impossible');
    } finally {
      setCoverUploading(false);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDelete = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce podcast ?')) return;
    
    try {
      await axios.delete(`/api/podcasts/${podcast.id}`);
      toast.success('Podcast supprimé');
      onDelete();
    } catch (error) {
      console.error('Error deleting podcast:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-violet-500/30 transition-all">
      {/* Hidden file input for cover image replacement */}
      {isOwnProfile && (
        <input
          ref={coverInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
          className="hidden"
          onChange={handleCoverChange}
          data-testid={`podcast-cover-input-${podcast.id}`}
        />
      )}
      {/* Cover Image avec Bouton Play */}
      <div className="relative aspect-video">
        {(localCoverUrl || podcast.cover_image_url) ? (
          <img
            src={localCoverUrl || getMediaUrl(podcast.cover_image_url)}
            alt={podcast.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              console.error('Podcast cover error:', podcast.cover_image_url);
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-violet-600 to-magenta-600 flex items-center justify-center">
            <Podcast className="w-20 h-20 text-white/50" />
          </div>
        )}

        {/* Play Button Overlay */}
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-all group"
        >
          {isPlaying ? (
            <Pause className="w-16 h-16 text-white group-hover:scale-110 transition-transform" />
          ) : (
            <Play className="w-16 h-16 text-white group-hover:scale-110 transition-transform" />
          )}
        </button>

        {/* Change-cover button — only visible to the owner */}
        {isOwnProfile && (
          <button
            onClick={(e) => { e.stopPropagation(); handleCoverPick(); }}
            disabled={coverUploading}
            className="absolute bottom-2 right-2 p-2 rounded-full bg-black/70 backdrop-blur-sm text-white hover:bg-violet-500/80 transition-colors disabled:opacity-50 flex items-center gap-1 z-10"
            title="Changer la couverture"
            data-testid={`change-podcast-cover-${podcast.id}`}
          >
            <Camera className="w-4 h-4" />
            <span className="text-xs">{coverUploading ? '...' : 'Couverture'}</span>
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2 gap-2">
          <h3 className="text-lg font-semibold flex-1 break-words">{podcast.title}</h3>
          {isOwnProfile && (
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <button
                onClick={handleDelete}
                className="p-2 text-red-400 hover:bg-red-500/10 rounded-full transition-colors"
                title="Supprimer"
                data-testid={`delete-podcast-${podcast.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={async () => {
                  const newTitle = window.prompt('Nouveau titre du podcast :', podcast.title || '');
                  if (newTitle === null) return;
                  const trimmed = newTitle.trim();
                  if (!trimmed) return toast.error('Titre vide');
                  try {
                    await axios.patch(`/api/podcasts/${podcast.id}`, { title: trimmed });
                    toast.success('Titre mis à jour');
                    onDelete?.();
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                }}
                className="p-2 text-blue-300 hover:bg-blue-500/10 rounded-full transition-colors"
                title="Renommer"
                data-testid={`rename-podcast-${podcast.id}`}
              >
                <Edit className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-3">
          <div 
            className="relative w-full h-2.5 bg-white/10 rounded-full cursor-pointer group"
            onClick={handleSeek}
            onTouchStart={(e) => {
              const touch = e.touches[0];
              const rect = e.currentTarget.getBoundingClientRect();
              const x = touch.clientX - rect.left;
              const percentage = Math.max(0, Math.min(1, x / rect.width));
              if (audioRef.current && duration) {
                audioRef.current.currentTime = percentage * duration;
                setCurrentTime(percentage * duration);
              }
            }}
          >
            <div 
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-violet-500 to-magenta-500 rounded-full transition-all"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            />
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full shadow-lg transition-all"
              style={{ left: `calc(${duration ? (currentTime / duration) * 100 : 0}% - 10px)` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-4 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <Heart className="w-3 h-3" />
            {podcast.likes_count || 0}
          </span>
          <span className="flex items-center gap-1">
            <Play className="w-3 h-3" />
            {podcast.plays_count || 0}
          </span>
          <span>{podcast.category}</span>
        </div>

        {/* Hidden Audio Element */}
        <audio
          ref={audioRef}
          src={getMediaUrl(podcast.audio_url)}
          onEnded={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onError={(e) => console.error('Podcast audio error:', podcast.audio_url)}
        />
      </div>
    </div>
  );
};

// Profile Article Card
const ProfileArticleCard = ({ article, isOwnProfile, onDelete }) => {
  const handleDelete = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet article ?')) return;
    
    try {
      await axios.delete(`/api/explorer/articles/${article.id}`);
      toast.success('Article supprimé');
      onDelete();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all">
      <div className="flex flex-col sm:flex-row">
        {article.cover_image_url && (
          <div className="sm:w-40 h-32 sm:h-auto flex-shrink-0">
            <img
              src={getMediaUrl(article.cover_image_url)}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-bold text-lg">{article.title}</h3>
            {isOwnProfile && (
              <button
                onClick={handleDelete}
                className="p-2 text-red-400 hover:bg-red-500/10 rounded-full transition-colors"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="text-sm text-gray-400 line-clamp-2 mb-2">{article.content?.slice(0, 150)}...</p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>{new Date(article.created_at).toLocaleDateString('fr-FR')}</span>
            {article.views > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {article.views} lectures
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Video Card
const ProfileVideoCard = ({ video, isOwnProfile, onDelete }) => {
  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;
    try {
      await axios.delete(`/api/explorer/videos/${video.id}`);
      toast.success('Vidéo supprimée');
      onDelete();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Get YouTube thumbnail if it's a YouTube video
  const getYouTubeThumbnail = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
    }
    return null;
  };

  const thumbnailUrl = video.thumbnail_url 
    ? getMediaUrl(video.thumbnail_url) 
    : getYouTubeThumbnail(video.video_url) || '/video-placeholder.png';

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-cyan-500/30 transition-all">
      <div className="flex flex-col sm:flex-row">
        {/* Thumbnail */}
        <div className="sm:w-48 h-32 sm:h-auto flex-shrink-0 relative">
          <img
            src={thumbnailUrl}
            alt={video.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = '/video-placeholder.png'; }}
          />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="w-10 h-10 bg-cyan-500/80 rounded-full flex items-center justify-center">
              <Play className="w-5 h-5 text-white ml-0.5" />
            </div>
          </div>
          {video.is_youtube && (
            <div className="absolute top-2 right-2 px-2 py-0.5 bg-red-600/90 rounded text-xs font-medium">
              YouTube
            </div>
          )}
        </div>
        
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-bold text-lg line-clamp-2">{video.title}</h3>
            {isOwnProfile && (
              <button
                onClick={handleDelete}
                className="p-2 text-red-400 hover:bg-red-500/10 rounded-full transition-colors ml-2"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          {video.description && (
            <p className="text-sm text-gray-400 line-clamp-2 mb-2">{video.description}</p>
          )}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>{new Date(video.created_at).toLocaleDateString('fr-FR')}</span>
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {video.views || 0} vues
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3" />
              {video.likes?.length || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

// ============= AUDIO STORY CARD (Studio audio stories) =============
const AudioStoryCard = ({ story, isOwnProfile, onDelete }) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer définitivement cette histoire audio ?")) return;
    try {
      await axios.delete(`/api/studio/stories/${story.id}`);
      toast.success("Histoire supprimée");
      if (onDelete) onDelete();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suppression impossible");
    }
  };
  const handleDownload = async (e) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`/api/studio/stories/${story.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'audio/mpeg' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (story.title || 'histoire').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 60) || 'histoire';
      a.download = `${safe}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Téléchargement lancé');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Téléchargement impossible');
    }
  };
  const isClosed = !!story.closed_at;
  const turnsCount = Array.isArray(story.turns) ? story.turns.length : 0;
  // Download allowed for creator or any participant.
  const myId = currentUser?.id;
  const isCreator = !!myId && story.creator_id === myId;
  const isParticipant = !!myId && Array.isArray(story.turns) && story.turns.some(t => t?.user_id === myId);
  const canDownload = turnsCount > 0 && (isCreator || isParticipant);
  return (
    <div className="bg-white/5 rounded-2xl p-4 border border-white/10" data-testid={`audio-story-card-${story.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-fuchsia-500 to-violet-600 flex items-center justify-center flex-shrink-0 shadow-lg">
          {isClosed ? <Lock className="w-7 h-7 text-white" /> : <BookOpen className="w-7 h-7 text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-semibold text-white truncate" data-testid={`audio-story-title-${story.id}`}>
              {story.title || 'Histoire sans titre'}
            </h3>
            {(canDownload || isOwnProfile) && (
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                {isOwnProfile && (
                  <button
                    onClick={handleDelete}
                    className="p-1.5 rounded-full hover:bg-red-500/20 text-red-400 transition-colors"
                    title="Supprimer"
                    data-testid={`audio-story-delete-${story.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {isOwnProfile && (
                  <button
                    onClick={async () => {
                      const newTitle = window.prompt('Nouveau titre de l\'histoire :', story.title || '');
                      if (newTitle === null) return;
                      const trimmed = newTitle.trim();
                      if (!trimmed) return toast.error('Titre vide');
                      try {
                        await axios.patch(`/api/studio/stories/${story.id}`, { title: trimmed });
                        toast.success('Titre mis à jour');
                        onDelete?.();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                    }}
                    className="p-1.5 rounded-full hover:bg-blue-500/20 text-blue-300 transition-colors"
                    title="Renommer"
                    data-testid={`audio-story-rename-${story.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                )}
                {canDownload && (
                  <button
                    onClick={handleDownload}
                    className="p-1.5 rounded-full hover:bg-emerald-500/20 text-emerald-300 transition-colors"
                    title="Télécharger l'histoire (MP3)"
                    data-testid={`audio-story-download-${story.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
          {story.synopsis && (
            <p className="text-xs text-gray-400 line-clamp-2 mt-0.5">{story.synopsis}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-2 flex-wrap">
            <span>{new Date(story.created_at).toLocaleDateString('fr-FR')}</span>
            <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{turnsCount} tour{turnsCount > 1 ? 's' : ''}</span>
            <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{story.likes_count || 0}</span>
            {isClosed && <span className="text-amber-400 font-medium">Clôturée</span>}
          </div>
          <button
            onClick={() => navigate(`/feed?tab=studio&story=${story.id}`)}
            className="mt-2 text-xs text-fuchsia-300 hover:text-white underline"
            data-testid={`audio-story-open-${story.id}`}
          >
            Ouvrir →
          </button>
        </div>
      </div>
    </div>
  );
};

